import { enContent } from "../content/locales/en";
import { svContent } from "../content/locales/sv";
import type { DescriptiveArticleContent } from "../content/articles";
import type { FortnoxPublicPageContent, FortnoxPublicPageKey } from "../content/fortnoxPublicPages";
import { buildFortnoxPublicJsonLd } from "../content/fortnoxPublicPages";
import { seoCatalog, type SeoRouteKey } from "../content/seoCatalog";
import { articleStructuredMediaAssetIds } from "../content/structuredMedia";
import {
  buildPrimarySoftwareApplicationOffer,
  buildSoftwareTierOfferCatalog,
  buildSoftwareTierOfferNodes,
  buildSoftwareTierProductNodes
} from "../content/softwarePricing";
import type { Locale } from "../content/types";
import {
  buildDynamicBooksStructuredMediaImageObjectForOrigin as buildStructuredMediaImageObjectForOrigin,
  buildDynamicBooksStructuredMediaUrlForOrigin as buildStructuredMediaUrlForOrigin,
  buildDynamicBooksOrganizationLogoImageObjectForOrigin as buildOrganizationLogoImageObjectForOrigin,
  getDynamicBooksStructuredMediaAsset as getStructuredMediaAsset,
  getDynamicBooksStructuredMediaFormat as getStructuredMediaFormat
} from "../../server/structuredMediaRegistry.js";
import {
  emptyPublicRatingsSummary,
  getDynamicBooksRatingsTarget,
  isPublicRatingsSummarySchemaEligible,
  normalizePublicRatingsSummary,
  type PublicRatingsSummary
} from "../lib/publicRatings";

export {
  buildDynamicBooksOrganizationLogoImageObjectForOrigin,
  buildDynamicBooksOrganizationLogoUrlForOrigin,
  buildDynamicBooksStructuredMediaImageObject,
  buildDynamicBooksStructuredMediaImageObjectForOrigin,
  buildDynamicBooksStructuredMediaUrl,
  buildDynamicBooksStructuredMediaUrlForLocale,
  buildDynamicBooksStructuredMediaUrlForOrigin,
  buildDynamicBooksStructuredMediaUrls,
  dynamicBooksOrganizationLogoAsset,
  dynamicBooksStructuredMediaAssets,
  dynamicBooksStructuredMediaHostForLocale,
  dynamicBooksStructuredMediaHostFromOrigin,
  dynamicBooksStructuredMediaOrigins,
  dynamicBooksStructuredMediaRegistry,
  getDynamicBooksStructuredMediaAsset,
  getDynamicBooksStructuredMediaFormat,
  listDynamicBooksStructuredMediaBySchemaUsage,
  type DynamicBooksOrganizationLogoAsset,
  type DynamicBooksStructuredMediaAssetEntry,
  type DynamicBooksStructuredMediaAssetId,
  type DynamicBooksStructuredMediaFamily,
  type DynamicBooksStructuredMediaFormat,
  type DynamicBooksStructuredMediaFormatEntry,
  type DynamicBooksStructuredMediaHost,
  type DynamicBooksStructuredMediaImageObject,
  type DynamicBooksStructuredMediaLocale,
  type DynamicBooksStructuredMediaSchemaUsage,
  type DynamicBooksStructuredMediaUsageRole
} from "../../server/structuredMediaRegistry.js";

export const seoBaseUrl = "https://staging.dynamicbooks.se";

const localeOgAssetId = {
  sv: "locale-og-sv",
  en: "locale-og-en"
} as const;

type RuntimeSeoConfig = {
  svBaseUrl: string;
  enBaseUrl: string;
  indexingEnabled: boolean;
};

const productionSvBaseUrl = "https://dynamicbooks.se";
const productionEnBaseUrl = "https://dynamicbooks.app";

export const seoOgImagePath = {
  sv: getStructuredMediaFormat(localeOgAssetId.sv, "png").publicPath,
  en: getStructuredMediaFormat(localeOgAssetId.en, "png").publicPath
} satisfies Record<Locale, string>;

const ogImageDimensions = {
  sv: {
    width: String(getStructuredMediaFormat(localeOgAssetId.sv, "png").width),
    height: String(getStructuredMediaFormat(localeOgAssetId.sv, "png").height)
  },
  en: {
    width: String(getStructuredMediaFormat(localeOgAssetId.en, "png").width),
    height: String(getStructuredMediaFormat(localeOgAssetId.en, "png").height)
  }
} satisfies Record<Locale, { width: string; height: string }>;

const contentByLocale = {
  sv: svContent,
  en: enContent
} satisfies Record<Locale, typeof svContent | typeof enContent>;

const ogLocale = {
  sv: "sv_SE",
  en: "en_US"
} satisfies Record<Locale, string>;

const alternateLocale = {
  sv: "en_US",
  en: "sv_SE"
} satisfies Record<Locale, string>;

const ogImageAlt = {
  sv: getStructuredMediaAsset(localeOgAssetId.sv).alt,
  en: getStructuredMediaAsset(localeOgAssetId.en).alt
} satisfies Record<Locale, string>;

const articlePrimaryImageAsset = getStructuredMediaFormat("article-agentdriven-bokforing-16x9", "png");
const articlePrimaryImageAlt = getStructuredMediaAsset("article-agentdriven-bokforing-16x9").alt;

export type JsonLdGraph = {
  "@context": "https://schema.org";
  "@graph": Array<Record<string, unknown>>;
};

export type RuntimeSeoMetadata = {
  locale: Locale;
  key: SeoRouteKey | DescriptiveArticleContent["key"] | FortnoxPublicPageKey | "crm" | "quickbooks";
  htmlLang: string;
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  alternateUrl: string | null;
  xDefaultUrl: string | null;
  alternates: Array<{
    hreflang: "sv" | "en" | "x-default";
    href: string;
  }>;
  robots: "noindex,nofollow" | "index,follow";
  og: {
    type: "website" | "article";
    siteName: "DynamicBooks";
    locale: string;
    alternateLocale: string | null;
    title: string;
    description: string;
    url: string;
    image: string;
    imageAlt: string;
    imageWidth: string;
    imageHeight: string;
  };
  twitter: {
    card: "summary_large_image";
    title: string;
    description: string;
    image: string;
    imageAlt: string;
  };
  jsonLd: JsonLdGraph;
};

export type RuntimeSeoMetadataOptions = {
  publicRatingsSummary?: PublicRatingsSummary | null;
};

export function absoluteSeoUrl(path: string, baseUrl = getRuntimeSeoConfig().svBaseUrl): string {
  return new URL(path, baseUrl).toString();
}

export function resolveSeoKeyFromHash(hash = ""): SeoRouteKey {
  switch (hash.replace(/^#/, "")) {
    case "services":
      return "services";
    case "accounting-agent":
      return "accounting-agent";
    case "data-rooms":
      return "data-rooms";
    case "reports":
      return "reports";
    case "software-pricing":
      return "pricing";
    case "estimate":
      return "estimate";
    case "faq":
      return "faq";
    case "login-handoff":
      return "login";
    default:
      return "home";
  }
}

export function buildRuntimeSeoMetadata(
  locale: Locale,
  key: SeoRouteKey = "home",
  options: RuntimeSeoMetadataOptions = {}
): RuntimeSeoMetadata {
  const config = getRuntimeSeoConfig();
  const content = contentByLocale[locale];
  const entry = seoCatalog[locale][key];
  const image = buildLocaleOgImageUrl(locale, config);
  const canonicalUrl = absoluteUrlForLocale(locale, entry.canonicalPath, config);
  const partnerLocale: Locale = locale === "sv" ? "en" : "sv";
  const alternateUrl = absoluteUrlForLocale(partnerLocale, entry.hreflangPartnerPath, config);
  const xDefaultUrl = absoluteUrlForLocale("sv", "/", config);

  return {
    locale,
    key,
    htmlLang: content.htmlLang,
    title: entry.title,
    description: entry.description,
    keywords: entry.keywords.join(", "),
    canonicalUrl,
    alternateUrl,
    xDefaultUrl,
    alternates: [
      {
        hreflang: "sv",
        href: locale === "sv" ? canonicalUrl : alternateUrl
      },
      {
        hreflang: "en",
        href: locale === "en" ? canonicalUrl : alternateUrl
      },
      {
        hreflang: "x-default",
        href: xDefaultUrl
      }
    ],
    robots: config.indexingEnabled ? "index,follow" : "noindex,nofollow",
    og: {
      type: "website",
      siteName: "DynamicBooks",
      locale: ogLocale[locale],
      alternateLocale: alternateLocale[locale],
      title: entry.title,
      description: entry.description,
      url: canonicalUrl,
      image,
      imageAlt: ogImageAlt[locale],
      imageWidth: ogImageDimensions[locale].width,
      imageHeight: ogImageDimensions[locale].height
    },
    twitter: {
      card: "summary_large_image",
      title: entry.title,
      description: entry.description,
      image,
      imageAlt: ogImageAlt[locale]
    },
    jsonLd: buildJsonLdGraph(locale, key, options)
  };
}

export function buildRuntimeArticleSeoMetadata(article: DescriptiveArticleContent): RuntimeSeoMetadata {
  const config = getRuntimeSeoConfig();
  const image = buildStructuredMediaUrlForOrigin(articleStructuredMediaAssetIds[0], "png", productionSvBaseUrl);

  return {
    locale: article.locale,
    key: article.key,
    htmlLang: article.htmlLang,
    title: article.title,
    description: article.metaDescription,
    keywords: article.keywords.join(", "),
    canonicalUrl: article.canonicalUrl,
    alternateUrl: null,
    xDefaultUrl: null,
    alternates: [],
    robots: config.indexingEnabled ? "index,follow" : "noindex,nofollow",
    og: {
      type: "article",
      siteName: "DynamicBooks",
      locale: ogLocale.sv,
      alternateLocale: null,
      title: article.title,
      description: article.metaDescription,
      url: article.canonicalUrl,
      image,
      imageAlt: articlePrimaryImageAlt,
      imageWidth: String(articlePrimaryImageAsset.width),
      imageHeight: String(articlePrimaryImageAsset.height)
    },
    twitter: {
      card: "summary_large_image",
      title: article.title,
      description: article.metaDescription,
      image,
      imageAlt: articlePrimaryImageAlt
    },
    jsonLd: buildArticleJsonLdGraph(article)
  };
}

export function buildRuntimeCrmSeoMetadata(locale: Locale): RuntimeSeoMetadata {
  const config = getRuntimeSeoConfig();
  const content = contentByLocale[locale];
  const canonicalUrl = new URL("/crm", baseUrlForLocale(locale, config)).toString();
  const image = buildLocaleOgImageUrl(locale, config);
  const title = "DynamicBooks CRM - SERVICEUPPSKATTNING";
  const description = "Internal DynamicBooks CRM workspace for SERVICEUPPSKATTNING cases.";

  return {
    locale,
    key: "crm",
    htmlLang: content.htmlLang,
    title,
    description,
    keywords: "DynamicBooks CRM, SERVICEUPPSKATTNING",
    canonicalUrl,
    alternateUrl: null,
    xDefaultUrl: null,
    alternates: [],
    robots: "noindex,nofollow",
    og: {
      type: "website",
      siteName: "DynamicBooks",
      locale: ogLocale[locale],
      alternateLocale: null,
      title,
      description,
      url: canonicalUrl,
      image,
      imageAlt: ogImageAlt[locale],
      imageWidth: ogImageDimensions[locale].width,
      imageHeight: ogImageDimensions[locale].height
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      image,
      imageAlt: ogImageAlt[locale]
    },
    jsonLd: buildCrmJsonLdGraph(locale, canonicalUrl, title, description)
  };
}

export function buildRuntimeFortnoxPublicSeoMetadata(page: FortnoxPublicPageContent): RuntimeSeoMetadata {
  const config = getRuntimeSeoConfig();
  const image = page.social.ogImage;

  return {
    locale: page.locale,
    key: page.key,
    htmlLang: page.locale,
    title: page.title,
    description: page.metaDescription,
    keywords: ["DynamicBooks", "Fortnox", page.localeTag, page.pageKind, page.key].join(", "),
    canonicalUrl: page.canonicalUrl,
    alternateUrl: page.locale === "sv"
      ? `https://dynamicbooks.se${page.path}?lang=en`
      : `https://dynamicbooks.se${page.path}`,
    xDefaultUrl: `https://dynamicbooks.se${page.path}`,
    alternates: [
      { hreflang: "sv", href: `https://dynamicbooks.se${page.path}` },
      { hreflang: "en", href: `https://dynamicbooks.se${page.path}?lang=en` },
      { hreflang: "x-default", href: `https://dynamicbooks.se${page.path}` }
    ],
    robots: config.indexingEnabled ? page.seo.robotsProduction : page.seo.robotsNonProduction,
    og: {
      type: "website",
      siteName: "DynamicBooks",
      locale: page.locale === "sv" ? ogLocale.sv : ogLocale.en,
      alternateLocale: page.locale === "sv" ? ogLocale.en : ogLocale.sv,
      title: page.title,
      description: page.metaDescription,
      url: page.canonicalUrl,
      image,
      imageAlt: page.social.imageAlt,
      imageWidth: page.social.width,
      imageHeight: page.social.height
    },
    twitter: {
      card: "summary_large_image",
      title: page.title,
      description: page.metaDescription,
      image: page.social.twitterImage,
      imageAlt: page.social.imageAlt
    },
    jsonLd: buildFortnoxPublicJsonLdGraph(page)
  };
}

export function buildRuntimeQuickBooksSeoMetadata(path: string, publicLanding: boolean): RuntimeSeoMetadata {
  const base = buildRuntimeSeoMetadata("en", "home");
  const canonicalUrl = new URL(path, productionEnBaseUrl).toString();
  const title = publicLanding
    ? "QuickBooks Online integration | DynamicBooks"
    : "Manage QuickBooks Online | DynamicBooks";
  const description = publicLanding
    ? "Review DynamicBooks QuickBooks Online scope, permissions, secure connection steps, and current production readiness."
    : "Sign in to review the server-reported status and protected controls for your DynamicBooks QuickBooks Online connection.";
  return {
    ...base,
    key: "quickbooks",
    title,
    description,
    keywords: "DynamicBooks, QuickBooks Online, accounting integration, Intuit OAuth, US companies",
    canonicalUrl,
    alternateUrl: null,
    xDefaultUrl: null,
    alternates: [],
    robots: publicLanding && base.robots === "index,follow" ? "index,follow" : "noindex,nofollow",
    og: { ...base.og, title, description, url: canonicalUrl, alternateLocale: null },
    twitter: { ...base.twitter, title, description },
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [{
        "@type": publicLanding ? "WebPage" : "ProfilePage",
        "@id": `${canonicalUrl}#page`,
        url: canonicalUrl,
        name: title,
        description
      }]
    }
  };
}

function buildCrmJsonLdGraph(
  locale: Locale,
  canonicalUrl: string,
  title: string,
  description: string
): JsonLdGraph {
  const config = getRuntimeSeoConfig();
  const rootUrl = absoluteUrlForLocale(locale, locale === "sv" ? "/" : "/en", config);
  const organizationId = `${rootUrl}#organization`;
  const websiteId = `${rootUrl}#website`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationNode(rootUrl, organizationId),
      {
        "@type": "WebSite",
        "@id": websiteId,
        name: "DynamicBooks",
        url: rootUrl,
        inLanguage: ["sv", "en"],
        publisher: {
          "@id": organizationId
        }
      },
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        description,
        inLanguage: contentByLocale[locale].htmlLang,
        isPartOf: {
          "@id": websiteId
        }
      }
    ]
  };
}

function buildFortnoxPublicJsonLdGraph(page: FortnoxPublicPageContent): JsonLdGraph {
  return buildFortnoxPublicJsonLd(page);
}

export function buildArticleJsonLdGraph(article: DescriptiveArticleContent): JsonLdGraph {
  const rootUrl = productionSvBaseUrl.endsWith("/") ? productionSvBaseUrl : `${productionSvBaseUrl}/`;
  const organizationId = `${rootUrl}#organization`;
  const websiteId = `${rootUrl}#website`;
  const webpageId = `${article.canonicalUrl}#webpage`;
  const articleId = `${article.canonicalUrl}#article`;
  const breadcrumbId = `${article.canonicalUrl}#breadcrumb`;
  const imageUrls = buildArticleImageUrls();
  const primaryImage = buildStructuredMediaImageObjectForOrigin(articleStructuredMediaAssetIds[0], "png", rootUrl);

  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationNode(rootUrl, organizationId),
      {
        "@type": "WebSite",
        "@id": websiteId,
        name: "DynamicBooks",
        url: rootUrl,
        inLanguage: ["sv"],
        publisher: {
          "@id": organizationId
        }
      },
      {
        "@type": "WebPage",
        "@id": webpageId,
        url: article.canonicalUrl,
        name: article.title,
        description: article.metaDescription,
        inLanguage: article.languageTag,
        isPartOf: {
          "@id": websiteId
        },
        mainEntity: {
          "@id": articleId
        },
        breadcrumb: {
          "@id": breadcrumbId
        },
        primaryImageOfPage: primaryImage
      },
      {
        "@type": "Article",
        "@id": articleId,
        url: article.canonicalUrl,
        headline: article.headline,
        description: article.metaDescription,
        articleSection: article.articleSection,
        inLanguage: article.languageTag,
        image: imageUrls,
        datePublished: article.datePublished,
        dateModified: article.dateModified,
        author: {
          "@id": organizationId
        },
        publisher: {
          "@id": organizationId
        },
        mainEntityOfPage: {
          "@id": webpageId
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbId,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: article.breadcrumb.homeLabel,
            item: rootUrl
          },
          {
            "@type": "ListItem",
            position: 2,
            name: article.breadcrumb.categoryLabel
          },
          {
            "@type": "ListItem",
            position: 3,
            name: article.breadcrumb.currentLabel,
            item: article.canonicalUrl
          }
        ]
      }
    ]
  };
}

function buildArticleImageUrls(): string[] {
  return articleStructuredMediaAssetIds.map((assetId) => buildStructuredMediaUrlForOrigin(assetId, "png", productionSvBaseUrl));
}

export function buildJsonLdGraph(
  locale: Locale,
  key: SeoRouteKey = "home",
  options: RuntimeSeoMetadataOptions = {}
): JsonLdGraph {
  const config = getRuntimeSeoConfig();
  const content = contentByLocale[locale];
  const entry = seoCatalog[locale][key];
  const canonicalUrl = absoluteUrlForLocale(locale, entry.canonicalPath, config);
  const rootUrl = absoluteUrlForLocale(locale, locale === "sv" ? "/" : "/en", config);
  const rootWithoutSlash = rootUrl.replace(/\/$/, "");
  const organizationId = `${rootUrl}#organization`;
  const websiteId = `${rootUrl}#website`;
  const serviceId = `${rootUrl}#professional-service`;
  const appId = `${rootUrl}#software-application`;
  const breadcrumbId = `${canonicalUrl}#breadcrumb`;
  const softwarePricingUrl = absoluteUrlForLocale(locale, locale === "sv" ? "/#software-pricing" : "/en#software-pricing", config);
  const softwareOfferCatalog = buildSoftwareTierOfferCatalog(locale, softwarePricingUrl, rootWithoutSlash);
  const softwareOfferNodes = buildSoftwareTierOfferNodes(locale, softwarePricingUrl, rootWithoutSlash);
  const softwareProductNodes = buildSoftwareTierProductNodes(locale, softwarePricingUrl, rootWithoutSlash);
  const softwareOverviewImage = buildStructuredMediaImageObjectForOrigin("software-overview", "png", rootUrl);
  const softwareApplicationImages = [softwareOverviewImage];
  const ratingsTarget = getDynamicBooksRatingsTarget(locale);
  const publicRatingsSummary = normalizePublicRatingsSummary(
    options.publicRatingsSummary ?? emptyPublicRatingsSummary(ratingsTarget),
    ratingsTarget
  );
  const softwareApplication: Record<string, unknown> = {
    "@type": "SoftwareApplication",
    "@id": appId,
    name: "DynamicBooks",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: canonicalUrl,
    image: softwareApplicationImages,
    offers: buildPrimarySoftwareApplicationOffer(locale, softwarePricingUrl, rootWithoutSlash),
    hasOfferCatalog: {
      "@id": softwareOfferCatalog["@id"]
    }
  };
  let aggregateRating: Record<string, unknown> | null = null;

  if (isPublicRatingsSummarySchemaEligible(publicRatingsSummary, ratingsTarget)) {
    aggregateRating = {
      "@type": "AggregateRating",
      "@id": `${rootWithoutSlash}/#software-aggregate-rating`,
      ratingValue: publicRatingsSummary.rating_value,
      ratingCount: publicRatingsSummary.rating_count,
      bestRating: publicRatingsSummary.best_rating,
      worstRating: publicRatingsSummary.worst_rating
    };
    softwareApplication.aggregateRating = aggregateRating;
    for (const productNode of softwareProductNodes) {
      productNode.aggregateRating = aggregateRating;
    }
  }

  const graph: Array<Record<string, unknown>> = [
    buildOrganizationNode(rootUrl, organizationId),
    {
      "@type": "WebSite",
      "@id": websiteId,
      name: "DynamicBooks",
      url: rootUrl,
      inLanguage: ["sv", "en"],
      publisher: {
        "@id": organizationId
      }
    },
    {
      "@type": "ProfessionalService",
      "@id": serviceId,
      name: "DynamicBooks",
      url: canonicalUrl,
      serviceType: entry.jsonLdIntent.serviceType,
      description: entry.description,
      areaServed: {
        "@type": "Country",
        name: entry.jsonLdIntent.areaServed
      },
      provider: {
        "@id": organizationId
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: content.services.title,
        itemListElement: content.services.items.map((service, index) => ({
          "@type": "Offer",
          position: index + 1,
          itemOffered: {
            "@type": "Service",
            name: service.title,
            description: service.body
          }
        }))
      }
    },
    softwareApplication,
    softwareOfferCatalog,
    ...softwareProductNodes,
    ...softwareOfferNodes,
    ...(aggregateRating ? [aggregateRating] : []),
    {
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: buildBreadcrumbItems(locale, key, rootUrl, canonicalUrl, entry.jsonLdIntent.breadcrumbName)
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: entry.title,
      description: entry.description,
      inLanguage: content.htmlLang,
      isPartOf: {
        "@id": websiteId
      },
      about: {
        "@id": serviceId
      },
      breadcrumb: {
        "@id": breadcrumbId
      },
      primaryImageOfPage: softwareOverviewImage
    }
  ];

  return {
    "@context": "https://schema.org",
    "@graph": graph
  };
}

function buildLocaleOgImageUrl(locale: Locale, config: RuntimeSeoConfig): string {
  return buildStructuredMediaUrlForOrigin(localeOgAssetId[locale], "png", baseUrlForLocale(locale, config));
}

function buildOrganizationNode(rootUrl: string, organizationId: string): Record<string, unknown> {
  return {
    "@type": "Organization",
    "@id": organizationId,
    name: "Eregis, Inc.",
    url: rootUrl,
    logo: buildOrganizationLogoImageObjectForOrigin(rootUrl),
    brand: {
      "@type": "Brand",
      name: "DynamicBooks",
      url: rootUrl
    }
  };
}

function getRuntimeSeoConfig(env = runtimeEnv(), hostname = runtimeHostname()): RuntimeSeoConfig {
  const productionHost = isSplitProductionHost(hostname);
  const explicitIndexing = normalizeBoolean(env.DYNAMICBOOKS_INDEXING_ENABLED);
  const indexingEnabled = explicitIndexing ?? (normalizeEnvName(env.DYNAMICBOOKS_ENV_NAME) === "prod" || productionHost);
  const publicBaseUrl = normalizeOrigin(env.DYNAMICBOOKS_PUBLIC_BASE_URL);
  const svBaseUrl = normalizeOrigin(env.DYNAMICBOOKS_SV_BASE_URL)
    ?? publicBaseUrl
    ?? (indexingEnabled ? productionSvBaseUrl : seoBaseUrl);
  const enBaseUrl = normalizeOrigin(env.DYNAMICBOOKS_EN_BASE_URL)
    ?? (indexingEnabled ? productionEnBaseUrl : svBaseUrl);

  return {
    svBaseUrl,
    enBaseUrl,
    indexingEnabled
  };
}

function absoluteUrlForLocale(locale: Locale, path: string, config: RuntimeSeoConfig): string {
  return new URL(pathForLocale(locale, path, config), baseUrlForLocale(locale, config)).toString();
}

function pathForLocale(locale: Locale, path: string, config: RuntimeSeoConfig): string {
  if (locale !== "en" || sameOrigin(config.svBaseUrl, config.enBaseUrl)) {
    return path;
  }
  return path.replace(/^\/en(?=\/|#|$)/, "/") || "/";
}

function baseUrlForLocale(locale: Locale, config: RuntimeSeoConfig): string {
  return locale === "en" ? config.enBaseUrl : config.svBaseUrl;
}

function runtimeEnv(): Record<string, string | undefined> {
  return ((globalThis as typeof globalThis & {
    process?: { env?: Record<string, string | undefined> };
  }).process?.env) ?? {};
}

function runtimeHostname(): string | null {
  return (globalThis as typeof globalThis & {
    location?: { hostname?: string };
  }).location?.hostname?.toLowerCase() ?? null;
}

function normalizeOrigin(value: string | null | undefined): string | null {
  const candidate = value?.trim();
  if (!candidate) {
    return null;
  }
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return null;
    }
    return parsed.origin;
  } catch {
    return null;
  }
}

function normalizeBoolean(value: string | undefined): boolean | null {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (["1", "true", "yes", "on"].includes(normalized)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(normalized)) {
    return false;
  }
  return null;
}

function normalizeEnvName(value: string | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

function isSplitProductionHost(hostname: string | null): boolean {
  return hostname === "dynamicbooks.se"
    || hostname === "www.dynamicbooks.se"
    || hostname === "dynamicbooks.app"
    || hostname === "www.dynamicbooks.app";
}

function sameOrigin(left: string, right: string): boolean {
  return normalizeOrigin(left) === normalizeOrigin(right);
}

export function serializeJsonLdForHtml(jsonLd: JsonLdGraph): string {
  return JSON.stringify(jsonLd).replace(/[<>&\u2028\u2029]/g, (character) => {
    switch (character) {
      case "<":
        return "\\u003c";
      case ">":
        return "\\u003e";
      case "&":
        return "\\u0026";
      case "\u2028":
        return "\\u2028";
      case "\u2029":
        return "\\u2029";
      default:
        return character;
    }
  });
}

export function applySeoMetadata(doc: Document, metadata: RuntimeSeoMetadata): void {
  doc.documentElement.lang = metadata.htmlLang;
  doc.documentElement.dir = "ltr";
  doc.title = metadata.title;

  upsertMeta(doc, 'meta[name="description"]', { name: "description", content: metadata.description });
  upsertMeta(doc, 'meta[name="keywords"]', { name: "keywords", content: metadata.keywords });
  upsertMeta(doc, 'meta[name="robots"]', { name: "robots", content: metadata.robots });
  upsertMeta(doc, 'meta[name="language"]', { name: "language", content: metadata.htmlLang });

  upsertLink(doc, 'link[rel="canonical"]', { rel: "canonical", href: metadata.canonicalUrl });
  syncAlternateLink(doc, "sv", metadata.alternates.find((alternate) => alternate.hreflang === "sv")?.href ?? null);
  syncAlternateLink(doc, "en", metadata.alternates.find((alternate) => alternate.hreflang === "en")?.href ?? null);
  syncAlternateLink(doc, "x-default", metadata.alternates.find((alternate) => alternate.hreflang === "x-default")?.href ?? null);

  upsertMeta(doc, 'meta[property="og:type"]', { property: "og:type", content: metadata.og.type });
  upsertMeta(doc, 'meta[property="og:site_name"]', { property: "og:site_name", content: metadata.og.siteName });
  upsertMeta(doc, 'meta[property="og:locale"]', { property: "og:locale", content: metadata.og.locale });
  syncOptionalMeta(doc, 'meta[property="og:locale:alternate"]', "property", "og:locale:alternate", metadata.og.alternateLocale);
  upsertMeta(doc, 'meta[property="og:title"]', { property: "og:title", content: metadata.og.title });
  upsertMeta(doc, 'meta[property="og:description"]', { property: "og:description", content: metadata.og.description });
  upsertMeta(doc, 'meta[property="og:url"]', { property: "og:url", content: metadata.og.url });
  upsertMeta(doc, 'meta[property="og:image"]', { property: "og:image", content: metadata.og.image });
  upsertMeta(doc, 'meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: metadata.og.image });
  upsertMeta(doc, 'meta[property="og:image:alt"]', { property: "og:image:alt", content: metadata.og.imageAlt });
  upsertMeta(doc, 'meta[property="og:image:width"]', { property: "og:image:width", content: metadata.og.imageWidth });
  upsertMeta(doc, 'meta[property="og:image:height"]', { property: "og:image:height", content: metadata.og.imageHeight });

  upsertMeta(doc, 'meta[name="twitter:card"]', { name: "twitter:card", content: metadata.twitter.card });
  upsertMeta(doc, 'meta[name="twitter:title"]', { name: "twitter:title", content: metadata.twitter.title });
  upsertMeta(doc, 'meta[name="twitter:description"]', { name: "twitter:description", content: metadata.twitter.description });
  upsertMeta(doc, 'meta[name="twitter:image"]', { name: "twitter:image", content: metadata.twitter.image });
  upsertMeta(doc, 'meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: metadata.twitter.imageAlt });

  upsertJsonLdScript(doc, serializeJsonLdForHtml(metadata.jsonLd));
}

function buildBreadcrumbItems(
  locale: Locale,
  key: SeoRouteKey,
  rootUrl: string,
  canonicalUrl: string,
  breadcrumbName: string
) {
  const homeName = "DynamicBooks";
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: homeName,
      item: rootUrl
    }
  ];

  if (key !== "home") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: breadcrumbName,
      item: canonicalUrl
    });
  }

  return items;
}

function syncAlternateLink(doc: Document, hreflang: "sv" | "en" | "x-default", href: string | null): void {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;

  if (!href) {
    doc.head.querySelector<HTMLLinkElement>(selector)?.remove();
    return;
  }

  upsertLink(doc, selector, { rel: "alternate", hreflang, href });
}

function syncOptionalMeta(
  doc: Document,
  selector: string,
  attributeName: "name" | "property",
  attributeValue: string,
  content: string | null
): void {
  if (!content) {
    doc.head.querySelector<HTMLMetaElement>(selector)?.remove();
    return;
  }

  upsertMeta(doc, selector, { [attributeName]: attributeValue, content });
}

function upsertMeta(doc: Document, selector: string, attributes: Record<string, string>) {
  let element = doc.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = doc.createElement("meta");
    doc.head.append(element);
  }

  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
}

function upsertLink(doc: Document, selector: string, attributes: Record<string, string>) {
  let element = doc.head.querySelector<HTMLLinkElement>(selector);

  if (!element) {
    element = doc.createElement("link");
    doc.head.append(element);
  }

  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
}

function upsertJsonLdScript(doc: Document, serializedJsonLd: string) {
  let script = doc.getElementById("dynamicbooks-jsonld") as HTMLScriptElement | null;

  if (!script) {
    script = doc.createElement("script");
    script.id = "dynamicbooks-jsonld";
    script.type = "application/ld+json";
    doc.head.append(script);
  }

  script.textContent = serializedJsonLd;
}
